import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useSelector } from "react-redux"
import { RiDeleteBin6Line } from "react-icons/ri"
import { MdEdit } from "react-icons/md"

import IconBtn from "../../../common/IconBtn"
import ConfirmationModal from "../../../common/ConfirmationModal"
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../../../services/operations/adminAPI"

//Categories are created by admins only, and every course has to sit under one,
//so until this page existed a category could only be added straight in the database.
const Categories = () => {
  const { token } = useSelector((state) => state.auth)

  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(null)

  //Holds the category currently being edited, or null while creating a new one
  const [editing, setEditing] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm()

  const loadCategories = async () => {
    setLoading(true)
    const result = await fetchCategories()
    setCategories(result)
    setLoading(false)
  }

  useEffect(() => {
    loadCategories()
  }, [])

  const onSubmit = async (data) => {
    const succeeded = editing
      ? await updateCategory(
          {
            categoryId: editing._id,
            name: data.name,
            description: data.description,
          },
          token
        )
      : await createCategory(
          { name: data.name, description: data.description },
          token
        )

    if (succeeded) {
      reset()
      setEditing(null)
      loadCategories()
    }
  }

  const startEditing = (category) => {
    setEditing(category)
    setValue("name", category.name)
    setValue("description", category.description)
  }

  const cancelEditing = () => {
    setEditing(null)
    reset()
  }

  const handleDelete = async (category) => {
    const succeeded = await deleteCategory({ categoryId: category._id }, token)

    if (succeeded) {
      loadCategories()
    }

    setConfirmationModal(null)
  }

  return (
    <div>
      <h1 className="mb-8 text-3xl font-medium text-richblack-5">
        Manage Categories
      </h1>

      {/* create / edit form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mb-10 flex flex-col gap-y-4 rounded-md border-[1px] border-richblack-700 bg-richblack-800 p-6"
      >
        <p className="text-lg font-semibold text-richblack-5">
          {editing ? "Edit category" : "Add a new category"}
        </p>

        <label className="flex flex-col gap-y-1">
          <span className="text-sm text-richblack-5">
            Name <sup className="text-pink-200">*</sup>
          </span>

          <input
            type="text"
            placeholder="Enter category name"
            {...register("name", { required: true })}
            className="form-style w-full rounded-md bg-richblack-700 p-3 text-richblack-5"
          />

          {errors.name && (
            <span className="text-xs text-pink-200">Name is required</span>
          )}
        </label>

        <label className="flex flex-col gap-y-1">
          <span className="text-sm text-richblack-5">
            Description <sup className="text-pink-200">*</sup>
          </span>

          <textarea
            placeholder="Enter description"
            {...register("description", { required: true })}
            className="form-style min-h-[100px] w-full resize-y rounded-md bg-richblack-700 p-3 text-richblack-5"
          />

          {errors.description && (
            <span className="text-xs text-pink-200">
              Description is required
            </span>
          )}
        </label>

        <div className="flex items-center gap-x-3">
          <IconBtn
            type="submit"
            disabled={isSubmitting}
            text={editing ? "Save changes" : "Create category"}
          />

          {editing && (
            <button
              type="button"
              onClick={cancelEditing}
              className="rounded-md bg-richblack-700 px-5 py-2 font-semibold text-richblack-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* existing categories */}
      {loading ? (
        <div className="spinner"></div>
      ) : categories.length === 0 ? (
        <p className="text-center text-xl font-medium text-richblack-100">
          No categories yet. Add the first one above.
        </p>
      ) : (
        <div className="flex flex-col gap-y-3">
          {categories.map((category) => (
            <div
              key={category._id}
              className="flex items-center justify-between gap-x-4 rounded-md border-[1px] border-richblack-700 bg-richblack-800 p-5"
            >
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold text-richblack-5">
                  {category.name}
                </p>

                <p className="mt-1 text-sm text-richblack-300">
                  {category.description}
                </p>

                <p className="mt-2 text-xs text-richblack-400">
                  {category.courses?.length || 0} course(s)
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-x-4">
                <button
                  title="Edit"
                  onClick={() => startEditing(category)}
                  className="text-lg text-caribbeangreen-300"
                >
                  <MdEdit />
                </button>

                <button
                  title="Delete"
                  onClick={() =>
                    setConfirmationModal({
                      text1: "Delete this category?",
                      text2:
                        "Categories that still have courses in them cannot be deleted.",
                      btn1Text: "Delete",
                      btn2Text: "Cancel",
                      btn1Handler: () => handleDelete(category),
                      btn2Handler: () => setConfirmationModal(null),
                    })
                  }
                  className="text-lg text-pink-200"
                >
                  <RiDeleteBin6Line />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmationModal && (
        <ConfirmationModal modalData={confirmationModal} />
      )}
    </div>
  )
}

export default Categories
